import { useState } from 'react'
import {
    LuCoffee,
    LuCompass,
    LuGamepad2,
    LuLightbulb,
    LuPalette,
    LuSparkles,
} from 'react-icons/lu'
import { FaYarn } from 'react-icons/fa'
import FloatingIcons from '../FloatingIcons'
import './About.css'

const fieldNotes = [
    {
        id: 'painting',
        label: 'Painting',
        Icon: LuPalette,
        title: 'A happy little mess.',
        note: "Some days I have a vision. Other days I'm just putting colors on a canvas and seeing what happens. Both are equally fun (and equally messy).",
    },
    {
        id: 'crocheting',
        label: 'Crocheting',
        Icon: FaYarn,
        title: 'Just one more row...',
        note: "Famous last words before I accidentally spend three more hours crocheting. There's something so satisfying about turning a ball of yarn into a little something.",
    },
    {
        id: 'escape-rooms',
        label: 'Escape rooms',
        Icon: LuLightbulb,
        title: 'Wait, I think I got it!',
        note: "Give me a locked door, a suspicious-looking painting, and a countdown timer, and I'm having the time of my life. Bonus points for a good plot twist.",
    },
    {
        id: 'gaming',
        label: 'Gaming',
        Icon: LuGamepad2,
        title: 'One more game, I promise.',
        note: "Whether I'm trying way too hard to win or just building something cute with friends, gaming is my favorite mix of friendly competition, questionable decisions, and good laughs.",
    },
    {
        id: 'exploring',
        label: 'Exploring',
        Icon: LuCompass,
        title: 'Taking the scenic route.',
        note: "New places, little hidden gems, spontaneous detours, and stopping to take approximately a hundred pictures. I love seeing what's out there, even if it's just somewhere around the corner.",
    },
    {
        id: 'matcha',
        label: 'Matcha',
        Icon: LuCoffee,
        title: 'Matcha made in heaven.',
        note: "Iced, hot, rose, mango... I'll try just about any matcha. Half the fun is finding cute little cafés, and the other half is convincing myself I don't need another one.",
    },
]

const About = () => {
    const [activeNote, setActiveNote] = useState(0)
    const { Icon: NoteIcon, title, note } = fieldNotes[activeNote]

    return (
        <section
            id="about"
            className="about-section section"
            aria-labelledby="about-title"
        >
            <FloatingIcons variant="about" />
            <div className="site-container">
                <div className="section-heading" data-reveal>
                    <div>
                        <p className="eyebrow">
                            02 / The person behind the projects
                        </p>
                        <h2 id="about-title" className="section-title">
                            A curious mind.
                            <br />
                            <span>A creative heart.</span>
                        </h2>
                    </div>
                    <p className="section-summary">
                        Part engineer, part artist, full-time curious human.
                        Here's a little more about me beyond the projects and
                        pixels.
                    </p>
                </div>
                <div className="about-grid">
                    <div className="about-personal" data-reveal>
                        <div className="portrait-scrapbook">
                            <p className="portrait-scrapbook__annotation">
                                this is me!
                                <svg
                                    viewBox="0 0 58 40"
                                    fill="none"
                                    aria-hidden="true"
                                >
                                    <path d="M4 4C16 2 39 6 43 29M32 23l12 9 7-12" />
                                </svg>
                            </p>
                            <figure className="portrait-card">
                                <span
                                    className="portrait-card__tape"
                                    aria-hidden="true"
                                />
                                <div className="portrait-card__photo">
                                    <img
                                        src={`${process.env.PUBLIC_URL}/josephine.png`}
                                        alt="Josephine Nguyen smiling in a pink jacket"
                                        width="734"
                                        height="756"
                                        loading="lazy"
                                    />
                                </div>
                                <figcaption>
                                    Hi, I'm Josephine
                                    <span>nice to meet you!</span>
                                </figcaption>
                                <span
                                    className="portrait-card__stamp"
                                    aria-hidden="true"
                                >
                                    <LuSparkles />
                                </span>
                            </figure>
                            <span
                                className="portrait-scrapbook__paint"
                                aria-hidden="true"
                            >
                                <span />
                                <span />
                                <span />
                            </span>
                        </div>

                        <blockquote className="about-quote">
                            &ldquo;Design with purpose.
                            <br />
                            Code with heart. Teach with hope.&rdquo;
                        </blockquote>

                        <div className="field-notes">
                            <div className="field-notes__heading">
                                <h3>Off the clock</h3>
                                <span>a few things that make me, me</span>
                            </div>
                            <div
                                className="field-notes__choices"
                                role="group"
                                aria-label="Choose a personal field note"
                            >
                                {fieldNotes.map(
                                    ({ id, label, Icon }, index) => (
                                        <button
                                            key={id}
                                            type="button"
                                            className={`field-notes__choice field-notes__choice--${id}`}
                                            aria-pressed={activeNote === index}
                                            aria-controls="personal-field-note"
                                            onClick={() => setActiveNote(index)}
                                        >
                                            <Icon aria-hidden="true" />
                                            <span>{label}</span>
                                        </button>
                                    )
                                )}
                            </div>
                            <div
                                id="personal-field-note"
                                className="field-notes__page"
                                aria-live="polite"
                                aria-atomic="true"
                            >
                                <div
                                    className="field-notes__page-content"
                                    key={activeNote}
                                >
                                    <h4>{title}</h4>
                                    <p>{note}</p>
                                    <NoteIcon
                                        className="field-notes__doodle"
                                        aria-hidden="true"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="about-copy" data-reveal>
                        <p className="about-copy__lead">
                            {' '}
                            I care about what technology can do{' '}
                            <em>for people.</em>
                        </p>
                        <p>
                            I'm a full-stack developer, product designer, and
                            educational technologist who loves bringing ideas to
                            life, especially when they make learning a little
                            more exciting.
                        </p>

                        <p>
                            These days, I work as the{' '}
                            <strong>
                                Digital Product Developer & Account Manager
                            </strong>{' '}
                            at MicroSociety Inc., where I get to combine coding,
                            design, and education to create tools for students
                            and teachers. I've helped bring projects like{' '}
                            <strong>MicroSociety&reg; 2.0</strong> and{' '}
                            <strong>4th Terra&reg;</strong> to life, learning
                            plenty along the way from the people who actually
                            use them.
                        </p>

                        <section
                            className="about-story-note"
                            aria-labelledby="about-story-title"
                        >
                            <h3
                                id="about-story-title"
                                className="about-story-note__label"
                            >
                                a happy full-circle moment
                            </h3>
                            <p className="about-story-note__intro">
                                My love for educational technology started long
                                before I knew how to write a single line of
                                code.
                            </p>
                            <p>
                                Back in elementary school, I was{' '}
                                <em>that kid</em> who genuinely looked forward
                                to logging onto math learning websites. I loved
                                the challenges, the little achievements, and the
                                excitement of beating my own scores. I spent
                                countless hours on <em>First in Math</em>,
                                setting records and eventually becoming a{' '}
                                <a
                                    href="https://www.inquirer.com/philly/education/20140504_Philadelphia_girl_makes_math_a_game_-_and_excels.html"
                                    title="Back when I was obsessed with problem sets and primes"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    national champion
                                </a>{' '}
                                (the first one to do so three years in a row)!
                            </p>
                            <p>
                                But somewhere along the way, I started thinking
                                about how cool it would be to make something
                                like that myself. Those programs made learning
                                feel like a game, and I wanted to create that
                                same excitement for other kids.
                            </p>
                            <p>
                                Fast forward a few years, and that's exactly the
                                kind of work I get to do now. My journey at
                                MicroSociety started as a co-op student, turned
                                into nearly a year of volunteering simply
                                because I loved what we were building, and
                                eventually brought me back full-time in 2024.
                            </p>
                            <p className="about-story-note__ending">
                                It's pretty surreal to think that the little
                                girl who couldn't wait to play math games is now
                                helping create learning experiences for the next
                                generation. A very happy full-circle moment,
                                indeed.
                            </p>
                        </section>
                    </div>
                </div>

                <div className="about-more" data-reveal>
                    <div className="about-education">
                        <p className="eyebrow">A foundation in curiosity</p>
                        <h3>B.S. in Software Engineering</h3>
                        <p>
                            Minors in Human-Computer Interaction and Linguistics
                        </p>
                        <em>Drexel University</em>
                        <span className="about-education__honor">
                            <LuSparkles aria-hidden="true" />
                            Summa Cum Laude
                        </span>
                    </div>

                    <div className="about-more__copy">
                        <p>
                            I've never been particularly good at sticking to
                            just one interest. In high school, I somehow found
                            myself involved in around 20 different clubs,
                            leading about half of them and even starting a few
                            of my own, including a Board Games Club and a Harry
                            Potter Literary Club (because of course those needed
                            to exist!). Somewhere between all the meetings,
                            projects, and probably overcommitting myself, I also
                            graduated as valedictorian.
                        </p>

                        <p>
                            Looking back, I think I've always just loved having
                            something new to learn, make, or be part of. That
                            curiosity has taken me in all sorts of directions,
                            from tutoring math and mentoring students to
                            analyzing crime statistics, studying software
                            engineering, and designing digital experiences.
                        </p>

                        <p className="about-copy__closing">
                            I still have a hard time choosing just one thing to
                            be interested in, and I don't think I'd have it any
                            other way.
                        </p>
                        <span className="about-copy__signature">
                            still curious, still making, and always wondering
                            what's next. <span aria-hidden="true">♡</span>
                        </span>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default About
